import type { messageCallback, InboundMessage } from 'ably';
import { useEffect } from 'react';

import { useAblyClient } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/hooks/useAblyClient';
import type { AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';
import { captureException } from 'hb-react/shared/utils/errorReporting';

interface UseSubscribeToAblyChannelParams {
  tokenType: AblyTokenType;
  channel: string;
  onMessage: messageCallback<InboundMessage>;
  enabled?: boolean;
  useHistory?: boolean;
}

export const useSubscribeToAblyChannel = ({
  tokenType,
  channel,
  onMessage,
  enabled = true,
  useHistory = false,
}: UseSubscribeToAblyChannelParams) => {
  const { ablyClient, ablyReady, ensureConnected } = useAblyClient(tokenType);

  // Connect the ably client only if we need it
  useEffect(() => {
    if (ablyClient && enabled) {
      ensureConnected();
    }
  }, [ablyClient, enabled, ensureConnected]);

  // Subscribe to the channel once the connection is ready
  useEffect(() => {
    if (enabled && ablyClient && ablyReady) {
      const ablyChannel = ablyClient.channels.get(channel);

      ablyChannel.subscribe(onMessage);

      // History is used whenever the channel subscribers might've missed a published message.
      if (useHistory) {
        ablyChannel
          .history({ limit: 20 })
          .then((page) => {
            // Process each historical message
            page.items.toReversed().forEach(onMessage);
          })
          .catch((err) => {
            if (err) {
              captureException(err);
            }
          });
      }

      return () => {
        ablyChannel.unsubscribe(onMessage);
      };
    }
  }, [ablyClient, ablyReady, channel, enabled, onMessage, useHistory]);
};
